import { useTranslation } from "react-i18next";
import { GenderLabLoader, QuizTabs } from "../../components";
import {
  Text,
  Stack,
  Button,
  Box,
  Flex,
  Title,
  Alert,
} from "@mantine/core";
import { useEffect, useState } from "react";
import {
  TQuiz,
  TForm,
  TSurveyAnswer,
} from "../../types/Aula";
import { UseFormReturnType } from "@mantine/form";
import { aulaService } from "../../api";
import { IconInfoCircle } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { isAnswered } from "../../utils/isAnswered";

export const SatisfactionSurvey = ({
  quiz,
  enrollmentId,
  form,
  handleSubmit,
}: {
  quiz: TQuiz | null;
  enrollmentId: string | undefined;
  form: UseFormReturnType<TForm>;
  handleSubmit: (values: TSurveyAnswer) => void;
}) => {
  const { t } = useTranslation();
  const [showIntermediateScreen, setShowIntermediateScreen] = useState<boolean>(true);
  const [surveyAnswers, setSurveyAnswers] = useState<TSurveyAnswer | null>(
    null
  );

  useEffect(() => {
    const fetchSatisfactionSurvey = async () => {
      if (enrollmentId) {
        try {
          const survey = await aulaService.getSatisfactionSurvey(enrollmentId);
          setSurveyAnswers(survey.data.satisfaction_survey || {});
        } catch (error: any) {
          if (error.response?.status === 404) {
            setSurveyAnswers({});
          } else {
            showNotification({
              title: "Error",
              message: error.response?.data?.error || "Ocurrió un error",
              color: "red",
            });
            console.error(error);
          }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchSatisfactionSurvey();
  }, [quiz]);

  const parsedValues = (values: TForm): TSurveyAnswer => {
    const parsed = Object.entries(values)
      .filter(([questionId]) => isAnswered(form, questionId))
      .reduce((acc, [question, option_choice]) => {
        acc[question] = option_choice as string;
        return acc;
      }, {} as TSurveyAnswer);
    return parsed;
  };

  if (
    surveyAnswers === null ||
    enrollmentId === undefined
  ) {
    return <GenderLabLoader />;
  }

  if (
    surveyAnswers !== null && Object.keys(surveyAnswers).length > 0
  ) {
    return (
      <Flex ta="center" justify="center">
        <Alert
          variant="light"
          mt="md"
          w="50%"
          color="blue"
          title="Felicitaciones 🏆"
          ta="justify"
          icon={<IconInfoCircle />}
        >
          Has completado la encuesta de satisfacción.
          <br />
          ¡Gracias por tu esfuerzo y dedicación!
        </Alert>
      </Flex>
    );
  }

  return (
    <>
      {quiz && (
        <>
          {showIntermediateScreen && (
            <Box>
              <Stack gap="xs">
                <Text size="lg" fw={700} ta="center">
                  {quiz.name}
                </Text>
                <Text size="sm">
                  <Text component="span" fw={500}>
                    {t("quiz.questions")}
                  </Text>{" "}
                  {quiz.questions.length}
                </Text>
                <Text size="sm" fw={500}>
                  {t("quiz.noTimeLimit")}
                </Text>
              </Stack>
              <Flex justify="center" mt="lg" gap="sm">
                <Button onClick={() => setShowIntermediateScreen(false)}>
                  {t("actions.start")}
                </Button>
              </Flex>
            </Box>
          )}
          {!showIntermediateScreen && (
            <Box>
              <Title order={3} ta="center">
                {quiz.name}
              </Title>
              <form
                onSubmit={form.onSubmit((values) => {
                  handleSubmit(parsedValues(values));
                })}
              >
                <QuizTabs
                  questions={quiz.questions}
                  form={form}
                />
              </form>
            </Box>
          )}
        </>
      )}
    </>
  );
};
